import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Slider } from '../ui/slider';
import { Trash2 } from 'lucide-react';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../ui/select';

const FONT_GROUPS = [
  { label: 'Standard', fonts: [
    { value: 'times', label: 'Times New Roman', css: 'PDFTimes' },
    { value: 'courier', label: 'Courier', css: 'PDFCourier' },
    { value: 'arial', label: 'Arial', css: 'PDFHelvetica' },
    { value: 'georgia', label: 'Georgia', css: 'PDFTimes' },
    { value: 'times-bold', label: 'Times Bold', css: 'PDFTimes' },
  ]},
  { label: 'Sans-Serif', fonts: [
    { value: 'inter', label: 'Inter', css: 'PDF-Inter' },
    { value: 'roboto', label: 'Roboto', css: 'PDF-Roboto' },
    { value: 'open-sans', label: 'Open Sans', css: 'PDF-Open-Sans' },
    { value: 'montserrat', label: 'Montserrat', css: 'PDF-Montserrat' },
    { value: 'lato', label: 'Lato', css: 'PDF-Lato' },
    { value: 'poppins', label: 'Poppins', css: 'PDF-Poppins' },
  ]},
  { label: 'Serif', fonts: [
    { value: 'lora', label: 'Lora', css: 'PDF-Lora' },
    { value: 'merriweather', label: 'Merriweather', css: 'PDF-Merriweather' },
    { value: 'playfair-display', label: 'Playfair Display', css: 'PDF-Playfair-Display' },
    { value: 'pt-serif', label: 'PT Serif', css: 'PDF-PT-Serif' },
    { value: 'eb-garamond', label: 'EB Garamond', css: 'PDF-EB-Garamond' },
  ]},
  { label: 'Monospace', fonts: [
    { value: 'roboto-mono', label: 'Roboto Mono', css: 'PDF-Roboto-Mono' },
    { value: 'fira-code', label: 'Fira Code', css: 'PDF-Fira-Code' },
    { value: 'source-code-pro', label: 'Source Code Pro', css: 'PDF-Source-Code-Pro' },
    { value: 'space-mono', label: 'Space Mono', css: 'PDF-Space-Mono' },
  ]},
  { label: 'Display', fonts: [
    { value: 'oswald', label: 'Oswald', css: 'PDF-Oswald' },
    { value: 'bebas-neue', label: 'Bebas Neue', css: 'PDF-Bebas-Neue' },
    { value: 'anton', label: 'Anton', css: 'PDF-Anton' },
    { value: 'cinzel', label: 'Cinzel', css: 'PDF-Cinzel' },
  ]},
  { label: 'Handwriting', fonts: [
    { value: 'caveat', label: 'Caveat', css: 'PDF-Caveat' },
    { value: 'pacifico', label: 'Pacifico', css: 'PDF-Pacifico' },
    { value: 'dancing-script', label: 'Dancing Script', css: 'PDF-Dancing-Script' },
    { value: 'shadows-into-light', label: 'Shadows Into Light', css: 'PDF-Shadows-Into-Light' },
  ]},
];

const FontOptions = () => (
  <>
    {FONT_GROUPS.map(group => (
      <SelectGroup key={group.label}>
        <SelectLabel>{group.label}</SelectLabel>
        {group.fonts.map(font => (
          <SelectItem key={font.value} value={font.value} style={{ fontFamily: font.css }}>
            {font.label}
          </SelectItem>
        ))}
      </SelectGroup>
    ))}
  </>
);

const TextTool = ({ textElements, onAddText, onUpdateText, onDeleteText, selectedTextId, onSelectText }) => {
  const [newText, setNewText] = useState('');
  const [fontSize, setFontSize] = useState(16);
  const [color, setColor] = useState('#000000');
  const [fontFamily, setFontFamily] = useState('times');

  const selectedText = textElements.find(t => t.id === selectedTextId);

  const handleAdd = () => {
    if (!newText.trim()) return;
    onAddText({ text: newText, fontSize, color, fontFamily });
    setNewText('');
  };

  const handleUpdateSelected = (updates) => {
    if (selectedTextId) {
      onUpdateText(selectedTextId, updates);
    }
  };

  // Get display font size from pdfFontSize (for the edit slider)
  const selectedFontSize = selectedText?.pdfFontSize || 16;

  return (
    <div className="space-y-4" data-testid="text-tool">
      {/* ADD NEW TEXT */}
      <div>
        <Label className="text-xs font-semibold tracking-[0.05em] uppercase text-muted-foreground">
          Add New Text
        </Label>
      </div>

      <div>
        <Label htmlFor="text-input">Text Content</Label>
        <Input
          id="text-input"
          data-testid="text-input"
          value={newText}
          onChange={(e) => setNewText(e.target.value)}
          placeholder="Type your text here..."
          className="mt-1.5"
          onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
        />
      </div>

      <div>
        <Label htmlFor="font-family">Font</Label>
        <Select value={fontFamily} onValueChange={setFontFamily}>
          <SelectTrigger className="mt-1.5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <FontOptions />
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Font Size: {fontSize}pt</Label>
        <Slider
          data-testid="font-size-slider"
          value={[fontSize]}
          onValueChange={(value) => setFontSize(value[0])}
          min={6}
          max={72}
          step={0.5}
          className="mt-2"
        />
      </div>

      <div>
        <Label htmlFor="text-color">Color</Label>
        <div className="flex gap-2 mt-1.5">
          <Input
            id="text-color"
            data-testid="text-color-input"
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-10 w-16 p-1"
          />
          <Input
            type="text"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="flex-1 h-10"
            placeholder="#000000"
          />
        </div>
      </div>

      <Button
        onClick={handleAdd}
        className="w-full"
        data-testid="add-text-button"
        disabled={!newText.trim()}
      >
        Click PDF to Place Text
      </Button>

      {/* EDIT SELECTED TEXT */}
      {selectedText && (
        <>
          <div className="border-t border-border pt-4 mt-4">
            <Label className="text-xs font-semibold tracking-[0.05em] uppercase text-muted-foreground">
              Edit Selected Text
            </Label>
          </div>

          <div>
            <Label htmlFor="edit-text">Text</Label>
            <Input
              id="edit-text"
              data-testid="edit-text-input"
              value={selectedText.text}
              onChange={(e) => handleUpdateSelected({ text: e.target.value })}
              className="mt-1.5"
            />
          </div>

          <div>
            <Label htmlFor="edit-font-family">Font</Label>
            <Select value={selectedText.fontFamily || 'times'} onValueChange={(value) => handleUpdateSelected({ fontFamily: value })}>
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <FontOptions />
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Size: {selectedFontSize}pt</Label>
            <Slider
              data-testid="edit-font-size-slider"
              value={[selectedFontSize]}
              onValueChange={(value) => handleUpdateSelected({ pdfFontSize: value[0] })}
              min={6}
              max={96}
              step={0.5}
              className="mt-2"
            />
          </div>

          <div>
            <Label htmlFor="edit-color">Color</Label>
            <div className="flex gap-2 mt-1.5">
              <Input
                id="edit-color"
                data-testid="edit-color-input"
                type="color"
                value={selectedText.color}
                onChange={(e) => handleUpdateSelected({ color: e.target.value })}
                className="h-10 w-16 p-1"
              />
              <Input
                type="text"
                value={selectedText.color}
                onChange={(e) => handleUpdateSelected({ color: e.target.value })}
                className="flex-1 h-10"
              />
            </div>
          </div>

          <Button
            onClick={() => onDeleteText(selectedTextId)}
            variant="destructive"
            className="w-full"
            data-testid="delete-text-button"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Text
          </Button>
        </>
      )}

      <div className="bg-accent/50 p-3 rounded-md space-y-1">
        <p className="text-xs font-semibold text-foreground">How to use:</p>
        <ul className="text-xs text-muted-foreground space-y-1 ml-3">
          <li>1. Type text and adjust font/size/color</li>
          <li>2. Click "Click PDF to Place Text"</li>
          <li>3. Click on the PDF page to place</li>
          <li>4. Click placed text to select and edit</li>
          <li>5. Press Delete key or use button to remove</li>
        </ul>
      </div>
    </div>
  );
};

export default TextTool;
